import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import { getLocationServicePlans, deactivateLocationServicePlan, extendLocationServicePlan } from "../../../../models/_location";
import moment from "moment";
import Modal from "react-responsive-modal";

interface LocationAddOnsProps {
  locationId: string;
  setShowModal: (show: boolean) => void;
  refreshTrigger?: number;
  onRefresh?: () => void;
}

export default function LocationAddOns({
  locationId,
  setShowModal,
  refreshTrigger,
  onRefresh,
}: LocationAddOnsProps) {
  const [addOns, setAddOns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state for deactivation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [planToDeactivate, setPlanToDeactivate] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  // Modal state for extension
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [planToExtend, setPlanToExtend] = useState<any | null>(null);
  const [extensionDays, setExtensionDays] = useState<string>("");
  const [extending, setExtending] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const plans = await getLocationServicePlans(locationId);
      setAddOns(plans || []);
    } catch (error) {
      console.error("Error fetching add-ons:", error);
      toast.error("Failed to fetch add-ons");
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  const handleDeactivateClick = (id: string) => {
    setPlanToDeactivate(id);
    setShowConfirmModal(true);
  };

  const confirmDeactivate = async () => {
    if (!planToDeactivate) return;

    setDeactivating(true);
    try {
      const res = await deactivateLocationServicePlan(planToDeactivate);
      if (res) {
        toast.success("Add-on deactivated successfully");
        fetchData();
        if (onRefresh) onRefresh();
      } else {
        toast.error("Failed to deactivate add-on");
      }
    } catch (error) {
      console.error("Error deactivating add-on:", error);
      toast.error("An error occurred while deactivating");
    } finally {
      setDeactivating(false);
      setShowConfirmModal(false);
      setPlanToDeactivate(null);
    }
  };

  const handleExtendExpiryDateClick = (addOn: any) => {
    setPlanToExtend(addOn);
    setExtensionDays("");
    setShowExtendModal(true);
  };

  const confirmExtension = async () => {
    if (!planToExtend || !extensionDays) {
      toast.error("Please enter the number of days");
      return;
    }

    const days = parseFloat(extensionDays);
    if (isNaN(days) || days <= 0) {
      toast.error("Please enter a valid number of days");
      return;
    }

    setExtending(true);
    try {
      const res = await extendLocationServicePlan(planToExtend.id, days);
      if (res) {
        toast.success("Add-on expiration extended successfully");
        fetchData();
        if (onRefresh) onRefresh();
        setShowExtendModal(false);
        setPlanToExtend(null);
        setExtensionDays("");
      } else {
        toast.error("Failed to extend add-on expiration");
      }
    } catch (error) {
      console.error("Error extending add-on expiry:", error);
      toast.error("An error occurred while extending expiry");
    } finally {
      setExtending(false);
    }
  };


  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-200px">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-xl-12 col-lg-12 col-md-12 col-12">
        <div className="card mt-9 mx-xl-4 h-90">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h3 className="fw-bolder my-2 ms-xl-9 ms-3 mt-9">Add-ons</h3>
            <div>
              <button
                className="btn btn-primary me-2"
                onClick={() => setShowModal(true)}
                title="Enroll Add-on"
              >
                <i className="fa fa-plus"></i>
              </button>
              <button
                className="btn btn-outline-secondary"
                title="Export Excel"
                onClick={() => toast.error("Export CSV functionality coming soon")}
              >
                <i className="fa fa-file-excel"></i> Export Excel
              </button>
            </div>
          </div>
          <div className="card-body">
            {addOns.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                  <thead>
                    <tr className="fw-bolder text-muted">
                      <th className="min-w-50px">#</th>
                      <th className="min-w-200px">Add-on Name</th>
                      <th className="min-w-150px">Entry Date</th>
                      <th className="min-w-150px">Expiry Date</th>
                      <th className="min-w-150px">Created By</th>
                      <th className="min-w-100px">Status</th>
                      <th className="min-w-100px text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addOns.map((addOn, idx) => (
                      <tr key={addOn.id}>
                        <td>
                          <span className="text-gray-800 fw-bold fs-6">
                            {idx + 1}
                          </span>
                        </td>
                        <td>
                          <span className="text-gray-800 fw-bold text-hover-primary fs-6">
                            {addOn.servicePlan?.name || addOn.servicePlanId}
                          </span>
                        </td>
                        <td>
                          <span className="text-gray-600 fw-semibold d-block fs-6">
                            {moment(addOn.entryDate).format("YYYY-MM-DD")}
                          </span>
                        </td>
                        <td>
                          <span className="text-gray-600 fw-semibold d-block fs-6">
                            {moment(addOn.expiryDate).format("YYYY-MM-DD")}
                          </span>
                        </td>
                        <td>
                          <span className="text-gray-600 fw-semibold d-block fs-6">
                            {addOn.createdBy ? `${addOn.createdBy.firstName} ${addOn.createdBy.lastName || ""}` : "System"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${addOn.status === "ACTIVE"
                              ? "badge-light-success"
                              : addOn.status === "PENDING"
                                ? "badge-light-warning"
                                : "badge-light-danger"
                              } fw-bold`}
                          >
                            {addOn.status}
                          </span>
                        </td>
                        <td className="text-end">
                          {addOn.status !== "INACTIVE" && (
                            <>
                              <button
                                className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-4"
                                title="Deactivate"
                                onClick={() => handleDeactivateClick(addOn.id)}
                              >
                                <i className="fa fa-ban fs-3 text-danger"></i>
                              </button>
                              <button
                                className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm"
                                title="Extend Expiry Date"
                                onClick={() => handleExtendExpiryDateClick(addOn)}
                              >
                                <i className="fa fa-plus fs-3 text-success"></i>
                              </button>
                            </>

                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="fs-6 text-muted">No records found</div>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={showConfirmModal}
        onClose={() => !deactivating && setShowConfirmModal(false)}
        center
        classNames={{
          root: "content-wrapper",
        }}
      >
        <div className="modal-header p-2 pb-4 mb-4">
          <h4 className="modal-title fs-1 mt-4">Confirm Deactivation</h4>
        </div>
        <div className="modal-body">
          <p className="fs-4">
            Are you sure you want to deactivate this add-on?
          </p>
        </div>
        <div className="modal-footer mt-6 p-0 justify-content-start">
          <button
            type="button"
            className="btn btn-primary me-4 fs-4 p-0 px-6 py-3 mt-6"
            onClick={confirmDeactivate}
            disabled={deactivating}
          >
            {deactivating ? "Deactivating..." : "Yes, deactivate"}
          </button>
          <button
            type="button"
            className="btn btn-secondary me-4 fs-4 p-0 px-6 py-3 mt-6"
            onClick={() => setShowConfirmModal(false)}
            disabled={deactivating}
          >
            Cancel
          </button>
        </div>
      </Modal>

      <Modal
        open={showExtendModal}
        onClose={() => !extending && setShowExtendModal(false)}
        center
        classNames={{
          root: "content-wrapper",
        }}
      >
        <div className="modal-header p-2 pb-4 mb-4">
          <h4 className="modal-title fs-1 mt-4">
            Extend Expiration : {planToExtend?.servicePlan?.name || planToExtend?.servicePlanId || ""}
          </h4>
        </div>
        <div className="modal-body">
          <div className="row mb-4">
            <label className="col-lg-6 col-md-6 fw-bold text-muted fs-6">
              Number of days to extend
            </label>
            <div className="col-lg-6 col-md-6 fv-row">
              <input
                type="number"
                className="form-control"
                placeholder="Enter days"
                value={extensionDays}
                onChange={(e) => setExtensionDays(e.target.value)}
                disabled={extending}
              />
              {planToExtend && extensionDays && parseFloat(extensionDays) > 0 && (
                <div className="mt-4 text-primary fw-bold">
                  New Expiry Date: {moment(planToExtend.expiryDate).add(parseFloat(extensionDays), 'days').format("YYYY-MM-DD")}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer mt-6 p-0 justify-content-start">
          <button
            type="button"
            className="btn btn-primary me-4 fs-4 p-0 px-6 py-3 mt-6"
            onClick={confirmExtension}
            disabled={extending}
          >
            {extending ? "Processing..." : "OK"}
          </button>
          <button
            type="button"
            className="btn btn-secondary me-4 fs-4 p-0 px-6 py-3 mt-6"
            onClick={() => setShowExtendModal(false)}
            disabled={extending}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
}
